import { Button } from "@bcc/components/ui/button";
import { cn } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import type { SelectionMode } from "./use-selection-popover";

interface SelectionPopoverPanelProps {
  panelRef: React.RefObject<HTMLDivElement>;
  testIdComponent: string;
  selectionMode: SelectionMode;
  setSelectionMode: (mode: SelectionMode) => void;
  onSelectAll: () => void;
  onSelectCurrentPage: () => void;
  onClear: () => void;
  onSpecificSubmit: () => void;
  specificQuantity: string;
  setSpecificQuantity: (value: string) => void;
  isApplyDisabled: boolean;
  isOverMax: boolean;
  allCount: number;
  currentPageCount: number;
  maxLimit: number;
  showMaxLabel: boolean;
}

export const SelectionPopoverPanel = (props: SelectionPopoverPanelProps) => {
  const {
    panelRef,
    testIdComponent,
    selectionMode,
    setSelectionMode,
    onSelectAll,
    onSelectCurrentPage,
    onClear,
    onSpecificSubmit,
    specificQuantity,
    setSpecificQuantity,
    isApplyDisabled,
    isOverMax,
    allCount,
    currentPageCount,
    maxLimit,
    showMaxLabel,
  } = props;

  const { t } = useTranslation();

  const setTestId = (description: string) => generateTestId(testIdComponent, description);

  const handleSelectAll = React.useCallback(() => {
    setSelectionMode("all");
    onSelectAll();
  }, [setSelectionMode, onSelectAll]);

  const handleSelectCurrentPage = React.useCallback(() => {
    setSelectionMode("currentPage");
    onSelectCurrentPage();
  }, [setSelectionMode, onSelectCurrentPage]);

  const handleSelectSpecific = React.useCallback(() => {
    setSelectionMode("specific");
  }, [setSelectionMode]);

  const handleQuantityChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSpecificQuantity(event.target.value);
    },
    [setSpecificQuantity]
  );

  const handleQuantityKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && !isApplyDisabled) onSpecificSubmit();
    },
    [onSpecificSubmit, isApplyDisabled]
  );

  const optionBase =
    "flex w-full items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground";

  const panel = (
    <div
      ref={panelRef}
      data-testid={setTestId("panel")}
      className="fixed z-50 min-w-[220px] rounded-md border border-border bg-popover shadow-lg"
    >
      {/* All orders */}
      <button
        type="button"
        data-testid={setTestId("option-all")}
        onClick={handleSelectAll}
        className={cn(optionBase, "rounded-t-md", selectionMode === "all" && "bg-accent font-medium")}
      >
        <span>
          {showMaxLabel ? t("dataTable.selectionPopover.allOrdersMax", { max: maxLimit }) : t("dataTable.selectionPopover.allOrders")}
        </span>
        <span className="tabular-nums text-muted-foreground">{allCount}</span>
      </button>

      {/* Current page only */}
      <button
        type="button"
        data-testid={setTestId("option-currentPage")}
        onClick={handleSelectCurrentPage}
        className={cn(optionBase, selectionMode === "currentPage" && "bg-accent font-medium")}
      >
        <span>{t("dataTable.selectionPopover.currentPageOnly")}</span>
        <span className="tabular-nums text-muted-foreground">{currentPageCount}</span>
      </button>

      {/* Select specific amount */}
      <div>
        <button
          type="button"
          data-testid={setTestId("option-specific")}
          onClick={handleSelectSpecific}
          className={cn(optionBase, selectionMode === "specific" && "bg-accent font-medium")}
        >
          {t("dataTable.selectionPopover.selectSpecificAmount")}
        </button>

        {selectionMode === "specific" && (
          <div className="flex items-center gap-2 px-3 pb-2">
            <input
              type="number"
              min={1}
              max={maxLimit}
              step={1}
              value={specificQuantity}
              onChange={handleQuantityChange}
              onKeyDown={handleQuantityKeyDown}
              aria-label={t("dataTable.selectionPopover.selectSpecificAmount")}
              placeholder={t("dataTable.selectionPopover.quantityPlaceholder", {
                max: maxLimit,
              })}
              data-testid={setTestId("quantity-input")}
              className="h-8 flex-1 rounded-sm border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <Button
              size="sm"
              disabled={isApplyDisabled}
              onClick={onSpecificSubmit}
              data-testid={setTestId("apply-btn")}
              className="h-8 shrink-0"
            >
              {t("dataTable.selectionPopover.apply")}
            </Button>
          </div>
        )}

        {isOverMax && (
          <p role="alert" data-testid={setTestId("max-error")} className="px-3 pb-2 text-xs text-destructive">
            {t("dataTable.selectionPopover.maxError", { max: maxLimit })}
          </p>
        )}
      </div>

      {/* Separator + Clear */}
      <div className="my-1 border-t border-border" />
      <button
        type="button"
        data-testid={setTestId("clear")}
        onClick={onClear}
        className={cn(optionBase, "rounded-b-md text-muted-foreground")}
      >
        {t("dataTable.selectionPopover.clearSelection")}
      </button>
    </div>
  );

  return <>{ReactDOM.createPortal(panel, document.body)}</>;
};
